/**
 * STUDIO INSPECT IA — como o sistema APARECE.
 *
 * Identidade, logos, tema, tipografia, login, layout, aparência documental,
 * templates visuais, bloco visual de assinatura e dashboard. Nada aqui muda
 * COMO o sistema funciona: workflow, permissões, ICP funcional, numeração,
 * regras de END, segurança e importação são de CONFIGURAÇÕES.
 *
 * Regras que atravessam o arquivo:
 *
 * 1. IDENTIDADE PADRÃO INSPECT IA. O nome do software é fixo e o padrão é
 *    sempre INSPECT IA. Temas de marca (ex.: o tema salvo EMTHOS, decisão
 *    do usuário de 2026-09-26) existem só como TEMA SALVO: ficam no sistema
 *    sem aplicação e são aplicados manualmente (rascunho → preview → publicar).
 * 2. OS PADRÕES SÃO O VISUAL DE HOJE. A versão V0 publicada reproduz
 *    exatamente a aplicação escura, o login atual e o padrão documental
 *    vigente — instalar o Studio não muda nada na tela nem no PDF.
 * 3. MUDAR NÃO REESCREVE O PASSADO. O visual de documento entra no MODELO
 *    documental quando ele é montado; o documento aprovado congela o modelo
 *    e, com ele, o visual da época. Documento anterior ao Studio (modelo sem
 *    visual) continua impresso como sempre foi.
 * 4. CONTROLE QUE NÃO MUDA NADA DIZ QUE NÃO MUDA (`STUDIO_EFFECTS`).
 */

export const OFFICIAL_SOFTWARE_NAME = "INSPECT IA";
export const OFFICIAL_SUBTITLE = "SISTEMA DE INSPEÇÃO E INTEGRIDADE MECÂNICA";

/**
 * Termos proibidos em texto do Studio. Vazio por decisão do usuário
 * (2026-09-26): a marca que antes era proibida passou a existir como TEMA
 * SALVO, aplicável manualmente. O mecanismo continua para uso futuro.
 */
export const FORBIDDEN_BRAND_TERMS: readonly string[] = [];

// ═══════════════════════════════════════════════════════════ seções

export type StudioSectionId =
  | "identity" | "logos" | "theme" | "typography" | "login" | "layout"
  | "documents" | "templates" | "signature" | "dashboard";

export const STUDIO_TABS: ReadonlyArray<{ id: string; number: string; label: string; section: StudioSectionId | "headerFooter" | "preview" | "history" }> = [
  { id: "identidade", number: "01", label: "Identidade INSPECT IA", section: "identity" },
  { id: "logos", number: "02", label: "Logos", section: "logos" },
  { id: "tema", number: "03", label: "Tema e Cores", section: "theme" },
  { id: "tipografia", number: "04", label: "Tipografia", section: "typography" },
  { id: "login", number: "05", label: "Tela de Login", section: "login" },
  { id: "layout", number: "06", label: "Layout da Plataforma", section: "layout" },
  { id: "relatorios", number: "07", label: "Relatórios", section: "documents" },
  { id: "cabecalho", number: "08", label: "Cabeçalhos e Rodapés", section: "headerFooter" },
  { id: "templates", number: "09", label: "Templates", section: "templates" },
  { id: "assinaturas", number: "10", label: "Assinaturas Visuais", section: "signature" },
  { id: "dashboard", number: "11", label: "Dashboard", section: "dashboard" },
  { id: "preview", number: "12", label: "Preview", section: "preview" },
  { id: "historico", number: "13", label: "Histórico Visual", section: "history" }
];

/** Área do histórico por seção (os termos do comando: "Logo alterado", "Tema alterado"...). */
export const HISTORY_AREA_OF_SECTION: Record<StudioSectionId, string> = {
  identity: "Identidade", logos: "Logo", theme: "Tema", typography: "Tipografia", login: "Login", layout: "Layout",
  documents: "Relatórios (cabeçalho e rodapé)", templates: "Template", signature: "Assinatura visual", dashboard: "Dashboard"
};

// ═══════════════════════════════════════════════════════════ tipos

export interface StudioAsset {
  /** Referência opaca do armazenamento oficial de arquivos — o binário NÃO fica no desenho. */
  ref: string;
  fileName: string;
  mimeType: string;
  size: number;
  sha256: string;
  uploadedAt: string;
  uploadedBy: string;
}

export interface IdentityDesign {
  /** Fixo: a identidade oficial é INSPECT IA. */
  softwareName: string;
  subtitle: string;
  slogan: string;
  description: string;
  versionLabel: string;
}

export type LogoSlot = "main" | "app" | "document" | "login" | "reduced" | "favicon" | "appIcon";
export const LOGO_SLOTS: ReadonlyArray<{ slot: LogoSlot; label: string; mimeTypes: readonly string[]; maxBytes: number; note: string }> = [
  { slot: "main", label: "Logo principal INSPECT IA", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 1_000_000, note: "Identidade institucional nas telas." },
  { slot: "app", label: "Logo da aplicação", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 1_000_000, note: "Tela de abertura enquanto a sessão é validada." },
  { slot: "document", label: "Logo documental INSPECT IA", mimeTypes: ["image/png", "image/jpeg"], maxBytes: 600_000, note: "Cabeçalho dos relatórios. Só PNG/JPEG — é o que o motor de PDF desenha." },
  { slot: "login", label: "Logo da tela de login", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 1_000_000, note: "Card de acesso." },
  { slot: "reduced", label: "Logo reduzido", mimeTypes: ["image/png", "image/jpeg", "image/webp"], maxBytes: 400_000, note: "Espaços pequenos." },
  { slot: "favicon", label: "Favicon", mimeTypes: ["image/png"], maxBytes: 200_000, note: "Aba do navegador." },
  { slot: "appIcon", label: "Ícone da aplicação", mimeTypes: ["image/png"], maxBytes: 400_000, note: "Atalho / ícone instalado." }
];
export type LogoDesign = Record<LogoSlot, StudioAsset | null>;

export interface ThemeDesign {
  /**
   * De qual tema do catálogo (`THEME_CATALOG`) esta paleta partiu. Editar uma
   * cor não troca o id: a tela compara a paleta com a do catálogo e diz
   * "personalizado".
   */
  themeId: string;
  mode: "ESCURO" | "CLARO" | "AUTOMATICO";
  /** Cor institucional (botão primário, elemento ativo). NÃO é a cor de erro. */
  primary: string;
  /** Superfície elevada / controle neutro. */
  secondary: string;
  /**
   * Superfície forte — o que as telas chamam de `--color-primary` (no app é
   * superfície #3C3C3C: cabeçalho de tabela, painéis), NÃO a cor institucional.
   */
  surfaceStrong: string;
  /** Principal no hover. */
  accent: string;
  /** Principal pressionado. */
  primaryActive: string;
  /** Texto sobre a cor principal (botão primário). */
  primaryText: string;
  background: string;
  cards: string;
  borders: string;
  borderStrong: string;
  textPrimary: string;
  textSecondary: string;
  /** Legenda, placeholder, texto desabilitado. */
  textMuted: string;
  /** Fundo dos campos de formulário. */
  inputBackground: string;
  /** Menu lateral e topo (elementos escuros). */
  sidebar: string;
  success: string;
  warning: string;
  /** Erro semântico — token separado da cor institucional, mesmo que pareçam. */
  danger: string;
  info: string;
}

/** As cores do tema, sem o modo e sem a origem. */
export type ThemePalette = Omit<ThemeDesign, "mode" | "themeId">;

export const FONT_OPTIONS: ReadonlyArray<{ id: string; label: string; stack: string }> = [
  { id: "INTER", label: "Inter (padrão INSPECT IA)", stack: 'Inter, "Segoe UI", Helvetica, Arial, sans-serif' },
  { id: "SEGOE", label: "Segoe UI", stack: '"Segoe UI", Inter, Helvetica, Arial, sans-serif' },
  { id: "ROBOTO", label: "Roboto (a mesma dos PDFs)", stack: 'Roboto, "Segoe UI", Helvetica, Arial, sans-serif' },
  { id: "HELVETICA", label: "Helvetica / Arial", stack: 'Helvetica, Arial, sans-serif' },
  { id: "SISTEMA", label: "Fonte do sistema operacional", stack: 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif' }
];

export interface TypographyDesign {
  bodyFont: string;
  headingFont: string;
  baseWeight: 400 | 500;
  scale: "COMPACTA" | "PADRAO" | "AMPLA";
  letterSpacing: "NORMAL" | "AMPLO";
}

export interface LoginDesign {
  /** ESCURO = o login técnico de hoje; CLARO = fundo cinza claro, texto grafite, card claro. */
  variant: "ESCURO" | "CLARO";
  /** Logo exibido ACIMA do título (painel institucional e card). null = só o título. */
  brandLogo: StudioAsset | null;
  title: string;
  subtitle: string;
  slogan: string;
  institutionalText: string;
  footerText: string;
  cardSubtitle: string;
  cardMotto: string;
  buttonLabel: string;
  cardPosition: "DIREITA" | "ESQUERDA" | "CENTRO";
  /** Intensidade do escurecimento sobre a imagem de fundo (1 = o de hoje). */
  overlayOpacity: number;
  backgroundColor: string;
  accentColor: string;
  /** null = o degradê dourado de hoje. */
  buttonColor: string | null;
  /** null = a imagem industrial padrão. */
  backgroundImage: StudioAsset | null;
}

export interface LayoutDesign {
  density: "COMPACTO" | "PADRAO" | "CONFORTAVEL";
  sidebarWidth: number;
  cardStyle: "PLANO" | "CONTORNO" | "ELEVADO";
  radius: number;
  spacing: "JUSTO" | "NORMAL" | "AMPLO";
}

/** `null` = a cor própria de cada modelo de relatório (o padrão vigente). */
export interface DocumentColors {
  headerText: string | null;
  sectionNumberFill: string | null;
  sectionNumberText: string | null;
  sectionTitleFill: string | null;
  sectionTitleText: string | null;
  tableHeaderFill: string | null;
  tableHeaderText: string | null;
  border: string | null;
}

export interface DocumentDesign {
  logoMode: "TEXTO" | "IMAGEM";
  colors: DocumentColors;
  qrPosition: "CENTRO" | "DIREITA";
  footerInstitutionalText: string;
  footerShowReportType: boolean;
}

export type TemplatedDocument = "RI" | "ME" | "LP" | "PM" | "USC" | "BSCAN" | "AVR" | "RDO" | "BOOK";
export const TEMPLATED_DOCUMENTS: readonly TemplatedDocument[] = ["RI", "ME", "LP", "PM", "USC", "BSCAN", "AVR", "RDO", "BOOK"];
export const TEMPLATED_DOCUMENT_LABELS: Record<TemplatedDocument, string> = {
  RI: "Relatório de Integridade", ME: "Medição de Espessura", LP: "Líquido Penetrante", PM: "Partículas Magnéticas",
  USC: "US-Convencional", BSCAN: "B-Scan / C-Scan", AVR: "AVR", RDO: "RDO", BOOK: "Book de Inspeção"
};

export type TemplateStatus = "RASCUNHO" | "EM_EDICAO" | "PUBLICADO" | "ARQUIVADO";
export const TEMPLATE_STATUS_LABELS: Record<TemplateStatus, string> = { RASCUNHO: "Rascunho", EM_EDICAO: "Em edição", PUBLICADO: "Publicado", ARQUIVADO: "Arquivado" };

export interface TemplateDesign {
  status: TemplateStatus;
  /** Sobre o padrão de Relatórios; `null` herda. */
  colors: DocumentColors;
  qrPosition: "HERDAR" | "CENTRO" | "DIREITA";
  footerInstitutionalText: string | null;
}

export interface SignatureVisualDesign {
  position: "FIM_DO_DOCUMENTO";
  showImage: boolean;
  showName: boolean;
  showRegistration: boolean;
  showFunction: boolean;
  showDate: boolean;
  /** Travado em NÃO: a assinatura é a imagem cadastrada; nenhum provedor ICP está integrado (Configurações). */
  showDigitalMark: boolean;
}

export interface DashboardDesign {
  cardStyle: "PLANO" | "CONTORNO" | "ELEVADO";
  density: "COMPACTO" | "PADRAO" | "CONFORTAVEL";
  indicatorStyle: "NUMERO" | "NUMERO_E_TENDENCIA";
  tableStyle: "LISA" | "ZEBRADA";
  hierarchy: "NEUTRA" | "DESTAQUE";
  engineering: {
    accent: string;
    accentSand: string;
    accentDone: string;
    accentWarn: string;
    accentInfo: string;
  };
}

export interface VisualDesign {
  identity: IdentityDesign;
  logos: LogoDesign;
  theme: ThemeDesign;
  typography: TypographyDesign;
  login: LoginDesign;
  layout: LayoutDesign;
  documents: DocumentDesign;
  templates: Record<TemplatedDocument, TemplateDesign>;
  signature: SignatureVisualDesign;
  dashboard: DashboardDesign;
}

// ═══════════════════════════════════════════════════════════ padrões

const noColors = (): DocumentColors => ({ headerText: null, sectionNumberFill: null, sectionNumberText: null, sectionTitleFill: null, sectionTitleText: null, tableHeaderFill: null, tableHeaderText: null, border: null });

/**
 * INSPECT IA — CLARO TÉCNICO (Tema Oficial Padrão de Fábrica)
 * Paleta de alto contraste, fundo cinza técnico (#D9DDE3), cards brancos (#FFFFFF),
 * tipografia grafite de alta legibilidade (#1A1D21) e acentos em vermelho técnico (#C62828).
 */
export const TECHNICAL_LIGHT_THEME: ThemePalette = {
  primary: "#C62828",
  accent: "#A61F1F",
  primaryActive: "#8A1818",
  primaryText: "#FFFFFF",
  secondary: "#C8CED6",
  surfaceStrong: "#C8CED6",
  background: "#D9DDE3",
  cards: "#FFFFFF",
  borders: "#5F6670",
  borderStrong: "#3D434B",
  textPrimary: "#1A1D21",
  textSecondary: "#48505B",
  textMuted: "#48505B",
  inputBackground: "#FFFFFF",
  sidebar: "#20262C",
  success: "#276B44",
  warning: "#8A5A00",
  danger: "#C62828",
  info: "#2F6690"
};

/** O tema escuro INSPECT IA de hoje — os valores dos tokens de `styles.css`. */
/**
 * O visual de hoje (styles.css). As cores novas (hover, ativo, texto sobre a
 * principal, borda forte, campo, menu, informação) têm como padrão os
 * valores que o styles.css já usa — instalar os tokens não muda nada.
 */
export const DEFAULT_DARK_THEME: ThemePalette = {
  primary: "#C7A75B", secondary: "#242C34", surfaceStrong: "#3C3C3C", accent: "#D6BF82", primaryActive: "#9C8248", primaryText: "#111418",
  background: "#111418", cards: "#1B2127", borders: "#323D47", borderStrong: "#9EA6AC",
  textPrimary: "#FFFFFF", textSecondary: "#B7B9BA", textMuted: "#A7AEB4", inputBackground: "#151B1F", sidebar: "#0E1216",
  success: "#C7A75B", warning: "#C7A75B", danger: "#FFFFFF", info: "#C7CDD2"
};

/** Paleta clara usada quando o modo CLARO (ou AUTOMÁTICO num sistema claro) está ativo. */
export const LIGHT_THEME: ThemePalette = {
  // Ouro escurecido: branco sobre ele passa de 3,7:1 para 5,5:1 (WCAG AA).
  primary: "#7A6536", secondary: "#E4E7EA", surfaceStrong: "#E4E7EA", accent: "#655429", primaryActive: "#54461F", primaryText: "#FFFFFF",
  background: "#F4F5F6", cards: "#FFFFFF", borders: "#D5D9DD", borderStrong: "#9EA6AC",
  textPrimary: "#111418", textSecondary: "#4A5560", textMuted: "#5C6670", inputBackground: "#FFFFFF", sidebar: "#1B2127",
  success: "#2F7A4A", warning: "#8A5A00", danger: "#B42318", info: "#2F6690"
};

/**
 * INDUSTRIAL VERMELHO — só a LINGUAGEM DE CORES da referência do usuário
 * (vermelho industrial + grafite + cinzas neutros + branco). Nenhum nome,
 * logo, texto ou marca da referência: o tema é INSPECT IA.
 *
 * Partiu da paleta pedida; ajustes feitos para contraste AA sobre branco e
 * sobre o fundo #F3F4F5 (conferidos em teste por `contrastRatio`):
 *  - texto atenuado #7A828A → #5F676F (3,9:1 → 5,8:1 sobre branco; legível
 *    também sobre o cabeçalho de tabela #E9ECEE);
 *  - status com tons próprios, legíveis sobre branco — o vermelho
 *    institucional (#D60000) e o vermelho de ERRO (#B3261E) são tokens
 *    distintos, como o comando exige.
 */
export const INDUSTRIAL_RED_THEME: ThemePalette = {
  primary: "#D60000", accent: "#BC0000", primaryActive: "#A50000", primaryText: "#FFFFFF",
  secondary: "#EEF0F2", surfaceStrong: "#E9ECEE", background: "#F3F4F5", cards: "#FFFFFF", borders: "#CDD2D6", borderStrong: "#A5ABB0",
  textPrimary: "#20262C", textSecondary: "#555E66", textMuted: "#5F676F", inputBackground: "#FFFFFF", sidebar: "#20262C",
  success: "#276B44", warning: "#8A5A00", danger: "#B3261E", info: "#2F6690"
};

export const DEFAULT_THEME_ID = "INSPECT_IA_CLARO_TECNICO";

export interface ThemeCatalogEntry {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly theme: ThemeDesign;
}

/**
 * TEMAS DE COR do Studio (aba 03). Aplicar um tema escreve SÓ a seção Tema
 * do RASCUNHO; nada muda no sistema ativo até PUBLICAR. O Claro Técnico é o
 * padrão oficial de fábrica e fallback do sistema.
 */
export const THEME_CATALOG: readonly ThemeCatalogEntry[] = [
  {
    id: DEFAULT_THEME_ID,
    name: "INSPECT IA — Claro Técnico",
    description: "Tema oficial padrão de fábrica. Fundo técnico cinza (#D9DDE3), cards brancos (#FFFFFF), tipografia grafite (#1A1D21) e acentos em vermelho técnico (#C62828).",
    theme: { themeId: DEFAULT_THEME_ID, mode: "CLARO", ...TECHNICAL_LIGHT_THEME }
  },
  {
    id: "INSPECT_IA_ORIGINAL",
    name: "INSPECT IA — Original",
    description: "Tema escuro técnico, grafite e ouro. Padrão escuro e tema usado quando selecionado.",
    theme: { themeId: "INSPECT_IA_ORIGINAL", mode: "ESCURO", ...DEFAULT_DARK_THEME }
  },
  {
    id: "INSPECT_IA_CLARO",
    name: "INSPECT IA — Claro",
    description: "Superfícies claras com o ouro INSPECT IA em tom escurecido para leitura sobre branco.",
    theme: { themeId: "INSPECT_IA_CLARO", mode: "CLARO", ...LIGHT_THEME }
  },
  {
    id: "INSPECT_IA_INDUSTRIAL_RED",
    name: "INSPECT IA — Industrial Vermelho",
    description: "Vermelho industrial, grafite e cinzas neutros sobre fundo claro. Menu e topo em grafite; sucesso, atenção, erro e informação com cores próprias.",
    theme: { themeId: "INSPECT_IA_INDUSTRIAL_RED", mode: "CLARO", ...INDUSTRIAL_RED_THEME }
  }
];

export function themeById(id: string): ThemeCatalogEntry | null {
  return THEME_CATALOG.find(t => t.id === id) ?? null;
}

export const THEME_COLOR_KEYS = Object.keys(DEFAULT_DARK_THEME) as ReadonlyArray<keyof ThemePalette>;

export function paletteOfTheme(theme: ThemeDesign): ThemePalette {
  return Object.fromEntries(THEME_COLOR_KEYS.map(k => [k, theme[k]])) as unknown as ThemePalette;
}

/** A paleta coincide com a do tema de origem? Se não, a tela chama de "personalizado". */
export function isThemeCustomized(theme: ThemeDesign): boolean {
  const origin = themeById(theme.themeId);
  if (!origin) return true;
  return origin.theme.mode !== theme.mode || THEME_COLOR_KEYS.some(k => origin.theme[k].toUpperCase() !== String(theme[k]).toUpperCase());
}

/**
 * Paleta EFETIVA: no modo CLARO sem cores próprias, vale a paleta clara;
 * no AUTOMÁTICO, segue a preferência do sistema operacional.
 */
export function effectivePalette(theme: ThemeDesign, prefersDark: boolean): ThemePalette {
  const configured = paletteOfTheme(theme);
  if (theme.mode === "ESCURO") return configured;
  if (theme.mode === "AUTOMATICO" && prefersDark) {
    return isLightPalette(configured) ? DEFAULT_DARK_THEME : configured;
  }
  if (theme.mode === "AUTOMATICO" && !prefersDark) {
    return !isLightPalette(configured) ? TECHNICAL_LIGHT_THEME : configured;
  }
  return configured;
}

function hexToRgba(hex: string, alpha: number): string {
  const [r, g, b] = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/** Superfície clara? (decide a camada de CSS do fundo e a regra de sombra). */
export function isLightPalette(p: ThemePalette): boolean {
  return relativeLuminance(p.background) > 0.5;
}

/**
 * DESIGN TOKENS — TODAS as variáveis CSS que dependem do tema, derivadas da
 * paleta semântica. Antes só parte delas acompanhava o tema (o texto
 * secundário, o fundo dos campos e os fundos de status ficavam escuros no
 * modo claro). A aplicação compara com a derivação da paleta de fábrica e
 * só grava o que difere — com o tema Original, nenhuma variável é tocada.
 *
 * `--ia-*` são os apelidos semânticos pedidos (definidos em styles.css
 * sobre estas mesmas variáveis); o menu tem token próprio.
 */
export function deriveThemeTokens(p: ThemePalette): Record<string, string> {
  const tokens: Record<string, string> = {};
  const put = (value: string, ...names: string[]) => { for (const n of names) tokens[n] = value; };
  // Institucional = os tokens de OURO (é o que .btn-primary usa). --color-primary é superfície no app.
  put(p.primary, "--accent-gold", "--color-gold-main", "--color-border-focus");
  put(p.surfaceStrong, "--color-primary");
  put(p.accent, "--color-primary-hover", "--accent-gold-hover", "--color-gold-light");
  put(p.primaryActive, "--color-primary-active", "--accent-gold-active", "--color-gold-deep");
  put(p.primaryText, "--color-primary-text", "--color-text-inverse");
  put(p.secondary, "--color-surface-secondary", "--color-surface-elevated", "--surface-elevated", "--color-interactive-default", "--color-graphite-elev");
  put(p.background, "--color-bg-primary", "--bg-graphite", "--color-bg-dark", "--color-surface-dark", "--color-graphite-base");
  put(p.cards, "--color-surface-primary", "--surface-graphite", "--color-bg-secondary", "--color-graphite-surf", "--color-interactive-active", "--color-interactive-disabled");
  put(p.borders, "--color-border-default", "--border-chrome-subtle", "--color-input-border", "--color-graphite-bord", "--color-border-subtle", "--color-interactive-hover");
  put(p.borderStrong, "--color-border-strong", "--border-chrome");
  put(p.textPrimary, "--color-text-primary", "--text-white");
  put(p.textSecondary, "--color-text-secondary", "--color-text-aux", "--text-silver", "--text-chrome");
  put(p.textMuted, "--color-text-muted", "--color-text-placeholder", "--color-text-disabled");
  put(p.inputBackground, "--color-input-bg");
  // Escala de grafite — as telas a usam como texto (300/400) e como fundo (700/800).
  put(p.background, "--color-graphite-800");
  put(p.borders, "--color-graphite-700");
  put(p.borderStrong, "--color-graphite-600");
  put(p.secondary, "--color-graphite-500");
  put(p.textMuted, "--color-graphite-400", "--color-graphite-200");
  put(p.textSecondary, "--color-graphite-300", "--color-graphite-100");
  put(p.sidebar, "--ia-sidebar-bg", "--ia-topbar-bg");
  put(p.success, "--status-success-solid", "--status-success-border");
  put(p.warning, "--status-warning-solid", "--status-warning-border");
  put(p.danger, "--status-danger-solid", "--status-danger-border");
  put(p.info, "--status-info-solid", "--status-info-border");
  put(hexToRgba(p.textPrimary, 0.06), "--color-surface-hover");
  put(hexToRgba(p.success, 0.14), "--status-success-bg");
  put(hexToRgba(p.warning, 0.14), "--status-warning-bg");
  put(hexToRgba(p.danger, 0.12), "--status-danger-bg");
  put(hexToRgba(p.info, 0.12), "--status-info-bg");
  put(p.textPrimary, "--status-success-text", "--status-warning-text", "--status-danger-text", "--status-info-text");
  // TOKENS ADAPTATIVOS — as cores que as telas tinham FIXAS no código foram
  // migradas para var(--ia-…, <cor antiga>). Estes tokens NÃO existem no
  // styles.css: sem tema publicado, vale a cor antiga (tema Original idêntico);
  // com um tema diferente, todas seguem a paleta.
  put(p.textPrimary, "--ia-fg");
  put(p.textSecondary, "--ia-fg-soft");
  put(p.textMuted, "--ia-fg-muted");
  put(p.inputBackground, "--ia-bg-inset");
  put(p.cards, "--ia-bg-panel");
  put(p.secondary, "--ia-bg-raised");
  put(p.borders, "--ia-line");
  put(p.success, "--ia-success-fg");
  put(p.warning, "--ia-warning-fg");
  put(p.danger, "--ia-danger-fg");
  put(p.info, "--ia-info-fg");
  put(hexToRgba(p.success, 0.12), "--ia-success-bg");
  put(hexToRgba(p.warning, 0.12), "--ia-warning-bg");
  put(hexToRgba(p.danger, 0.10), "--ia-danger-bg");
  put(hexToRgba(p.info, 0.10), "--ia-info-bg");
  // Botões de AÇÃO que usavam --color-primary (superfície) como fundo.
  put(p.primary, "--ia-action-bg");
  put(p.primaryText, "--ia-action-fg");
  // Acentos discretos do Painel de Engenharia (§49), quando o Studio não os personaliza.
  put(p.textMuted, "--ia-eng-accent");
  put(p.warning, "--ia-eng-accent-sand", "--ia-eng-accent-warn");
  put(p.success, "--ia-eng-accent-done");
  put(p.info, "--ia-eng-accent-info");
  return tokens;
}

/** Os textos da tela de login de hoje. `\n` = quebra de linha. */
export const DEFAULT_LOGIN: LoginDesign = {
  variant: "ESCURO",
  brandLogo: null,
  title: OFFICIAL_SOFTWARE_NAME,
  subtitle: "Sistema de Inspeção e Integridade Mecânica",
  slogan: "CONFIABILIDADE\nEM CADA DETALHE",
  institutionalText: "Inspeção, relatórios, gestão da integridade\ne controle técnico para um futuro mais seguro.",
  footerText: "INSPECT IA | TECNOLOGIA A FAVOR DA CONFIABILIDADE",
  cardSubtitle: "Sistema de Inspeção e Integridade Mecânica",
  cardMotto: "INTEGRIDADE HOJE.\nOPERAÇÕES MAIS SEGURAS AMANHÃ.",
  buttonLabel: "Entrar no Sistema",
  cardPosition: "DIREITA",
  overlayOpacity: 1,
  backgroundColor: "#0A0D11",
  accentColor: "#C9A227",
  buttonColor: null,
  backgroundImage: null
};

export function defaultVisualDesign(): VisualDesign {
  const templates = Object.fromEntries(TEMPLATED_DOCUMENTS.map(t => [t, { status: "PUBLICADO", colors: noColors(), qrPosition: "HERDAR", footerInstitutionalText: null }])) as Record<TemplatedDocument, TemplateDesign>;
  return {
    identity: { softwareName: OFFICIAL_SOFTWARE_NAME, subtitle: OFFICIAL_SUBTITLE, slogan: "TECNOLOGIA A FAVOR DA CONFIABILIDADE", description: "Plataforma de inspeção, integridade mecânica, engenharia e gestão documental.", versionLabel: "V-11" },
    logos: { main: null, app: null, document: null, login: null, reduced: null, favicon: null, appIcon: null },
    theme: { themeId: DEFAULT_THEME_ID, mode: "CLARO", ...TECHNICAL_LIGHT_THEME },
    typography: { bodyFont: "INTER", headingFont: "INTER", baseWeight: 400, scale: "PADRAO", letterSpacing: "NORMAL" },
    login: { ...DEFAULT_LOGIN },
    layout: { density: "PADRAO", sidebarWidth: 260, cardStyle: "CONTORNO", radius: 4, spacing: "NORMAL" },
    documents: { logoMode: "TEXTO", colors: noColors(), qrPosition: "CENTRO", footerInstitutionalText: "", footerShowReportType: true },
    templates,
    signature: { position: "FIM_DO_DOCUMENTO", showImage: true, showName: true, showRegistration: true, showFunction: true, showDate: true, showDigitalMark: false },
    dashboard: {
      cardStyle: "CONTORNO", density: "PADRAO", indicatorStyle: "NUMERO", tableStyle: "LISA", hierarchy: "NEUTRA",
      engineering: { accent: "#8C99A6", accentSand: "#B29455", accentDone: "#6E9E7E", accentWarn: "#B98A5E", accentInfo: "#7E93A8" }
    }
  };
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);

function deepMerge<T>(defaults: T, stored: unknown): T {
  if (!isObject(defaults) || !isObject(stored)) return (stored === undefined ? defaults : stored) as T;
  const out: Record<string, unknown> = { ...(defaults as Record<string, unknown>) };
  for (const key of Object.keys(defaults as Record<string, unknown>)) {
    if (!(key in stored)) continue;
    const def = (defaults as Record<string, unknown>)[key];
    const value = stored[key];
    out[key] = isObject(def) && isObject(value) ? deepMerge(def, value) : value;
  }
  return out as T;
}

/** Gravado + padrões. Só as chaves conhecidas entram; o nome do software volta sempre a INSPECT IA. */
export function normalizeVisualDesign(stored: unknown): VisualDesign {
  const design = deepMerge(defaultVisualDesign(), stored ?? {});
  design.identity.softwareName = OFFICIAL_SOFTWARE_NAME;
  design.signature.showDigitalMark = false;
  design.signature.position = "FIM_DO_DOCUMENTO";
  return design;
}

// ═══════════════════════════════════════════════════════════ validação

const HEX = /^#[0-9a-fA-F]{6}$/;
const oneOf = <T>(v: unknown, list: readonly T[]) => list.includes(v as T);

/** Todo texto do valor (recursivo), para a regra de marca externa. */
function allStrings(value: unknown, out: string[] = []): string[] {
  if (typeof value === "string") out.push(value);
  else if (Array.isArray(value)) value.forEach(v => allStrings(v, out));
  else if (isObject(value)) Object.entries(value).forEach(([k, v]) => { if (k !== "ref" && k !== "sha256") allStrings(v, out); });
  return out;
}

export function containsForbiddenBrand(value: unknown): string | null {
  for (const text of allStrings(value)) {
    const normalized = text.normalize("NFD").replace(/[̀-ͯ]/g, "").toUpperCase();
    const hit = FORBIDDEN_BRAND_TERMS.find(term => normalized.includes(term));
    if (hit) return hit;
  }
  return null;
}

function relativeLuminance(hex: string): number {
  const c = [1, 3, 5].map(i => parseInt(hex.slice(i, i + 2), 16) / 255).map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * c[0]! + 0.7152 * c[1]! + 0.0722 * c[2]!;
}

/** Razão de contraste WCAG entre duas cores #RRGGBB. */
export function contrastRatio(a: string, b: string): number {
  if (!HEX.test(a) || !HEX.test(b)) return 21;
  const [l1, l2] = [relativeLuminance(a), relativeLuminance(b)].sort((x, y) => y - x) as [number, number];
  return (l1 + 0.05) / (l2 + 0.05);
}

function checkColors(e: string[], prefix: string, obj: Record<string, unknown>, nullable: boolean) {
  for (const [k, v] of Object.entries(obj)) {
    if (nullable && v === null) continue;
    if (typeof v !== "string" || !HEX.test(v)) e.push(`${prefix}: a cor "${k}" deve estar no formato #RRGGBB${nullable ? " (ou vazia para usar a do modelo)" : ""}.`);
  }
}

function checkText(e: string[], label: string, v: unknown, max: number, required = false) {
  if (typeof v !== "string") { e.push(`${label}: texto inválido.`); return; }
  if (required && !v.trim()) e.push(`${label}: obrigatório.`);
  if (v.length > max) e.push(`${label}: até ${max} caracteres.`);
  if (/[<>]/.test(v)) e.push(`${label}: não use os caracteres < e >.`);
}

function checkAsset(e: string[], label: string, v: unknown, mimeTypes: readonly string[]) {
  if (v === null) return;
  if (!isObject(v) || typeof v["ref"] !== "string" || !v["ref"]) { e.push(`${label}: arquivo inválido.`); return; }
  if (!mimeTypes.includes(String(v["mimeType"]))) e.push(`${label}: tipo ${String(v["mimeType"])} não aceito aqui (${mimeTypes.join(", ")}).`);
}

/** Erros que impedem gravar a seção no rascunho. */
export function validateStudioSection(section: StudioSectionId, value: unknown): string[] {
  const e: string[] = [];
  if (!isObject(value)) return ["Conteúdo da seção inválido."];
  const brand = containsForbiddenBrand(value);
  if (brand) e.push(`A identidade é exclusiva INSPECT IA: "${brand}" não pode aparecer no Studio.`);
  const v = value as Record<string, any>;
  switch (section) {
    case "identity":
      if (v["softwareName"] !== OFFICIAL_SOFTWARE_NAME) e.push("O nome do software é INSPECT IA e não é editável.");
      checkText(e, "Subtítulo", v["subtitle"], 120, true);
      checkText(e, "Slogan", v["slogan"], 120);
      checkText(e, "Descrição institucional", v["description"], 400);
      checkText(e, "Identificação da versão", v["versionLabel"], 30);
      break;
    case "logos":
      for (const s of LOGO_SLOTS) checkAsset(e, s.label, v[s.slot], s.mimeTypes);
      break;
    case "theme":
      if (!oneOf(v["mode"], ["ESCURO", "CLARO", "AUTOMATICO"])) e.push("Tema padrão: Escuro, Claro ou Automático.");
      if (typeof v["themeId"] !== "string" || !themeById(v["themeId"])) e.push("Tema de origem desconhecido.");
      checkColors(e, "Tema", Object.fromEntries(Object.entries(v).filter(([k]) => k !== "mode" && k !== "themeId")), false);
      for (const k of THEME_COLOR_KEYS) if (!(k in v)) e.push(`Tema: a cor "${k}" está faltando.`);
      break;
    case "typography":
      if (!FONT_OPTIONS.some(f => f.id === v["bodyFont"])) e.push("Fonte principal: escolha uma das fontes oficiais.");
      if (!FONT_OPTIONS.some(f => f.id === v["headingFont"])) e.push("Fonte de títulos: escolha uma das fontes oficiais.");
      if (!oneOf(v["baseWeight"], [400, 500])) e.push("Peso padrão: 400 ou 500.");
      if (!oneOf(v["scale"], ["COMPACTA", "PADRAO", "AMPLA"])) e.push("Escala tipográfica inválida.");
      if (!oneOf(v["letterSpacing"], ["NORMAL", "AMPLO"])) e.push("Espaçamento de letras inválido.");
      break;
    case "login":
      checkText(e, "Título", v["title"], 60, true);
      if (typeof v["title"] === "string" && !v["title"].toUpperCase().includes("INSPECT IA")) e.push("O título do login deve manter a identidade INSPECT IA.");
      for (const [k, label, max] of [["subtitle", "Subtítulo", 120], ["slogan", "Slogan", 120], ["institutionalText", "Texto institucional", 300], ["footerText", "Rodapé", 160], ["cardSubtitle", "Subtítulo do card", 120], ["cardMotto", "Lema do card", 160]] as const) checkText(e, label, v[k], max);
      checkText(e, "Botão Entrar", v["buttonLabel"], 40, true);
      if (!oneOf(v["cardPosition"], ["DIREITA", "ESQUERDA", "CENTRO"])) e.push("Posição do card: direita, esquerda ou centro.");
      if (typeof v["overlayOpacity"] !== "number" || v["overlayOpacity"] < 0 || v["overlayOpacity"] > 1) e.push("Transparência: de 0 a 1.");
      checkColors(e, "Login", { backgroundColor: v["backgroundColor"], accentColor: v["accentColor"] }, false);
      if (v["buttonColor"] !== null) checkColors(e, "Login", { buttonColor: v["buttonColor"] }, false);
      checkAsset(e, "Imagem de fundo", v["backgroundImage"], ["image/png", "image/jpeg", "image/webp"]);
      checkAsset(e, "Logo da marca no login", v["brandLogo"], ["image/png", "image/jpeg", "image/webp"]);
      if (!oneOf(v["variant"], ["ESCURO", "CLARO"])) e.push("Variante do login: escura ou clara.");
      break;
    case "layout":
      if (!oneOf(v["density"], ["COMPACTO", "PADRAO", "CONFORTAVEL"])) e.push("Densidade inválida.");
      if (typeof v["sidebarWidth"] !== "number" || !Number.isInteger(v["sidebarWidth"]) || v["sidebarWidth"] < 220 || v["sidebarWidth"] > 320) e.push("Largura do menu: de 220 a 320 px — fora disso o menu corta os títulos ou espreme as telas.");
      if (!oneOf(v["cardStyle"], ["PLANO", "CONTORNO", "ELEVADO"])) e.push("Estilo de cards inválido.");
      if (typeof v["radius"] !== "number" || !Number.isInteger(v["radius"]) || v["radius"] < 0 || v["radius"] > 12) e.push("Raio de borda: de 0 a 12 px.");
      if (!oneOf(v["spacing"], ["JUSTO", "NORMAL", "AMPLO"])) e.push("Espaçamento inválido.");
      break;
    case "documents":
      if (!oneOf(v["logoMode"], ["TEXTO", "IMAGEM"])) e.push("Logo documental: texto INSPECT IA ou imagem.");
      if (!isObject(v["colors"])) e.push("Cores do documento inválidas."); else checkColors(e, "Documento", v["colors"], true);
      if (!oneOf(v["qrPosition"], ["CENTRO", "DIREITA"])) e.push("Posição do QR: centro ou direita.");
      checkText(e, "Texto institucional do rodapé", v["footerInstitutionalText"], 160);
      if (typeof v["footerShowReportType"] !== "boolean") e.push("Tipo do relatório no rodapé: sim ou não.");
      break;
    case "templates":
      for (const t of TEMPLATED_DOCUMENTS) {
        const tpl = v[t];
        if (!isObject(tpl)) { e.push(`${t}: template ausente.`); continue; }
        if (!oneOf(tpl["status"], ["RASCUNHO", "EM_EDICAO", "PUBLICADO", "ARQUIVADO"])) e.push(`${t}: estado inválido.`);
        if (!isObject(tpl["colors"])) e.push(`${t}: cores inválidas.`); else checkColors(e, `Template ${t}`, tpl["colors"], true);
        if (!oneOf(tpl["qrPosition"], ["HERDAR", "CENTRO", "DIREITA"])) e.push(`${t}: posição do QR inválida.`);
        if (tpl["footerInstitutionalText"] !== null) checkText(e, `Template ${t} — rodapé`, tpl["footerInstitutionalText"], 160);
      }
      break;
    case "signature":
      for (const k of ["showImage", "showName", "showRegistration", "showFunction", "showDate"]) if (typeof v[k] !== "boolean") e.push(`${k}: sim ou não.`);
      if (v["showDigitalMark"] === true) e.push("\"Assinado digitalmente\" não pode ser exibido: a assinatura é a imagem cadastrada e nenhum provedor ICP está integrado (Configurações → Assinatura Digital).");
      if (!v["showName"]) e.push("O nome do signatário não pode ser ocultado — sem ele o bloco não identifica quem assina.");
      break;
    case "dashboard":
      if (!oneOf(v["cardStyle"], ["PLANO", "CONTORNO", "ELEVADO"])) e.push("Estilo dos cards inválido.");
      if (!oneOf(v["density"], ["COMPACTO", "PADRAO", "CONFORTAVEL"])) e.push("Densidade inválida.");
      if (!oneOf(v["indicatorStyle"], ["NUMERO", "NUMERO_E_TENDENCIA"])) e.push("Estilo de indicador inválido.");
      if (!oneOf(v["tableStyle"], ["LISA", "ZEBRADA"])) e.push("Aparência das tabelas inválida.");
      if (!oneOf(v["hierarchy"], ["NEUTRA", "DESTAQUE"])) e.push("Hierarquia visual inválida.");
      if (!isObject(v["engineering"])) e.push("Painel de Engenharia inválido."); else checkColors(e, "Painel de Engenharia", v["engineering"], false);
      break;
  }
  return e;
}

/** Avisos que não impedem gravar (legibilidade). */
export function studioWarnings(design: VisualDesign): string[] {
  const w: string[] = [];
  const t = design.theme;
  for (const issue of themeContrastIssues(t)) w.push(`Tema: ${issue}`);
  const c = design.documents.colors;
  if (c.sectionNumberFill && c.sectionNumberText && contrastRatio(c.sectionNumberFill, c.sectionNumberText) < 4.5) w.push("Relatórios: número da seção pouco legível sobre a faixa.");
  if (c.tableHeaderFill && c.tableHeaderText && contrastRatio(c.tableHeaderFill, c.tableHeaderText) < 4.5) w.push("Relatórios: cabeçalho de tabela pouco legível.");
  if (design.documents.logoMode === "IMAGEM" && !design.logos.document) w.push("Relatórios: modo IMAGEM sem logo documental enviado — o cabeçalho usará o texto INSPECT IA.");
  return w;
}

/**
 * Pares que PRECISAM ser legíveis (WCAG AA, 4,5:1 para texto). O menu é
 * sempre desenhado com texto claro, por isso a cor do menu é conferida
 * contra o branco.
 */
export const THEME_CONTRAST_PAIRS: ReadonlyArray<{ fg: keyof ThemePalette | "#F7F8F8"; bg: keyof ThemePalette; label: string; min: number }> = [
  { fg: "textPrimary", bg: "background", label: "texto sobre o fundo", min: 4.5 },
  { fg: "textPrimary", bg: "cards", label: "texto sobre os cards", min: 4.5 },
  { fg: "textSecondary", bg: "cards", label: "texto secundário sobre os cards", min: 4.5 },
  { fg: "textSecondary", bg: "background", label: "texto secundário sobre o fundo", min: 4.5 },
  { fg: "textMuted", bg: "cards", label: "texto atenuado sobre os cards", min: 4.5 },
  { fg: "textPrimary", bg: "inputBackground", label: "texto dos campos", min: 4.5 },
  { fg: "textMuted", bg: "surfaceStrong", label: "texto do cabeçalho de tabela", min: 4.5 },
  { fg: "textPrimary", bg: "surfaceStrong", label: "texto sobre a superfície forte", min: 4.5 },
  { fg: "primaryText", bg: "primary", label: "texto do botão principal", min: 4.5 },
  { fg: "primaryText", bg: "accent", label: "texto do botão principal no hover", min: 4.5 },
  { fg: "#F7F8F8", bg: "sidebar", label: "texto do menu", min: 4.5 },
  { fg: "danger", bg: "cards", label: "cor de erro sobre os cards", min: 3 },
  { fg: "success", bg: "cards", label: "cor de sucesso sobre os cards", min: 3 },
  { fg: "warning", bg: "cards", label: "cor de atenção sobre os cards", min: 3 },
  { fg: "info", bg: "cards", label: "cor de informação sobre os cards", min: 3 }
];

/** Pares abaixo do mínimo, já em português. Vazio = paleta legível. */
export function themeContrastIssues(theme: ThemeDesign): string[] {
  const out: string[] = [];
  for (const pair of THEME_CONTRAST_PAIRS) {
    const fg = pair.fg.startsWith("#") ? pair.fg : String(theme[pair.fg as keyof ThemePalette]);
    const bg = String(theme[pair.bg]);
    const ratio = contrastRatio(fg, bg);
    if (ratio < pair.min) out.push(`contraste baixo no ${pair.label} (${ratio.toFixed(1)}:1; mínimo ${pair.min.toString().replace(".", ",")}:1).`);
  }
  return out;
}

/** Tudo o que impede PUBLICAR: nenhuma alteração incompleta vai para o sistema ativo. */
export function validateForPublish(design: VisualDesign): string[] {
  const errors: string[] = [];
  for (const section of ["identity", "logos", "theme", "typography", "login", "layout", "documents", "templates", "signature", "dashboard"] as const) {
    errors.push(...validateStudioSection(section, design[section]));
  }
  for (const t of TEMPLATED_DOCUMENTS) {
    if (design.templates[t].status === "EM_EDICAO") errors.push(`Template ${TEMPLATED_DOCUMENT_LABELS[t]} está EM EDIÇÃO — conclua (Publicado) ou volte para Rascunho antes de publicar.`);
  }
  return errors;
}

// ═══════════════════════════════════════════════════════ o que muda o quê

export interface StudioEffect { applied: boolean; where: string }
const APP = (where: string): StudioEffect => ({ applied: true, where });
const REG = (why: string): StudioEffect => ({ applied: false, where: why });

export const STUDIO_EFFECTS: Readonly<Record<string, StudioEffect>> = {
  "identity.subtitle": APP("Tela de abertura e cabeçalho dos relatórios que imprimem o subtítulo."),
  "identity.slogan": REG("Nenhuma tela exibe o slogan fora do login (que tem campo próprio)."),
  "identity.description": REG("Texto de referência; o login tem o seu texto institucional."),
  "identity.versionLabel": APP("Tela de abertura."),
  "logos.main": REG("Nenhuma tela interna exibe logo institucional hoje."),
  "logos.app": APP("Tela de abertura (validando sessão)."),
  "logos.document": APP("Cabeçalho de ME, LP, PM, US, B-Scan e AVR (modo IMAGEM)."),
  "logos.login": APP("Card da tela de login."),
  "logos.reduced": REG("Nenhum espaço reduzido usa logo hoje."),
  "logos.favicon": APP("Aba do navegador."),
  "logos.appIcon": APP("Ícone de atalho (apple-touch-icon)."),
  "theme.*": APP("Tokens do shell e de todas as telas que usam os tokens de styles.css."),
  "typography.*": APP("Fonte e escala de toda a aplicação (base em rem)."),
  "login.*": APP("Tela de login."),
  "layout.density": APP("Escala base da aplicação."),
  "layout.sidebarWidth": APP("Largura do menu lateral."),
  "layout.radius": APP("Raio dos componentes que usam os tokens."),
  "layout.cardStyle": APP("Cards que usam a classe de card global."),
  "layout.spacing": APP("Espaçamentos que usam os tokens."),
  "documents.logoMode": APP("Cabeçalho de ME, LP, PM, US, B-Scan e AVR."),
  "documents.qrPosition": APP("Cabeçalho de ME, LP, PM, US, B-Scan e AVR."),
  "documents.colors.headerText": APP("Cabeçalho de ME, LP, PM, US, B-Scan e AVR."),
  "documents.colors": APP("Faixas de seção e cabeçalhos de tabela de PM, US e B-Scan. ME, LP, AVR e RI mantêm as próprias até serem ligados."),
  "documents.footerInstitutionalText": APP("Rodapé de PM, US e B-Scan."),
  "documents.footerShowReportType": APP("Rodapé de PM, US e B-Scan."),
  "templates.*": APP("Por tipo: PM, US, B-Scan (cores, QR e rodapé); ME, LP e AVR (QR e cor do cabeçalho). RI e RDO: registrado."),
  "signature.*": APP("Bloco de signatários de PM, US e B-Scan."),
  "dashboard.engineering": APP("Cores do Painel de Engenharia."),
  "dashboard.cardStyle": REG("O Painel e a Visão Geral têm estilos próprios; ainda não leem esta opção."),
  "dashboard.density": REG("Idem."),
  "dashboard.indicatorStyle": REG("Idem — e nunca muda o cálculo."),
  "dashboard.tableStyle": REG("Idem."),
  "dashboard.hierarchy": REG("Idem.")
};

export function studioEffectOf(section: string, field: string): StudioEffect | null {
  return STUDIO_EFFECTS[`${section}.${field}`] ?? STUDIO_EFFECTS[`${section}.*`] ?? null;
}

// ═══════════════════════════════════════════════════ visual documental

/**
 * O visual que vai DENTRO do modelo documental. Resolvido na montagem do
 * modelo (a imagem do logo já como dataURL, porque o builder é síncrono) e
 * congelado com ele quando o documento é aprovado.
 */
export interface DocumentVisual {
  designVersion: number;
  logoText: string;
  subtitle: string;
  logoImage: string | null;
  colors: DocumentColors;
  qrPosition: "CENTRO" | "DIREITA";
  footerInstitutionalText: string;
  footerShowReportType: boolean;
  signature: Omit<SignatureVisualDesign, "position" | "showDigitalMark">;
}

/** Padrão + template PUBLICADO do tipo (os demais estados não se aplicam a documento). */
export function documentVisualOf(design: VisualDesign, type: TemplatedDocument | null, designVersion: number, logoImage: string | null): DocumentVisual {
  const base = design.documents;
  const tpl = type ? design.templates[type] : null;
  const useTpl = !!tpl && tpl.status === "PUBLICADO";
  const colors = { ...base.colors } as DocumentColors;
  if (useTpl) for (const [k, v] of Object.entries(tpl.colors) as Array<[keyof DocumentColors, string | null]>) if (v) colors[k] = v;
  return {
    designVersion,
    logoText: OFFICIAL_SOFTWARE_NAME,
    subtitle: design.identity.subtitle,
    logoImage: base.logoMode === "IMAGEM" ? logoImage : null,
    colors,
    qrPosition: useTpl && tpl.qrPosition !== "HERDAR" ? tpl.qrPosition : base.qrPosition,
    footerInstitutionalText: useTpl && tpl.footerInstitutionalText !== null ? tpl.footerInstitutionalText : base.footerInstitutionalText,
    footerShowReportType: base.footerShowReportType,
    signature: { showImage: design.signature.showImage, showName: design.signature.showName, showRegistration: design.signature.showRegistration, showFunction: design.signature.showFunction, showDate: design.signature.showDate }
  };
}

// ═══════════════════════════════════════════════════════════ histórico

export interface StudioHistoryEntry {
  id: string;
  at: string;
  userId: string;
  userName: string;
  area: string;
  action: "RASCUNHO_SALVO" | "ARQUIVO_ENVIADO" | "RASCUNHO_DESCARTADO" | "PUBLICADO" | "RESTAURADO";
  detail: string;
  version: number | null;
}

export const HISTORY_ACTION_LABELS: Record<StudioHistoryEntry["action"], string> = {
  RASCUNHO_SALVO: "Rascunho salvo", ARQUIVO_ENVIADO: "Arquivo enviado", RASCUNHO_DESCARTADO: "Rascunho descartado", PUBLICADO: "Publicado", RESTAURADO: "Versão restaurada"
};

/** Seções que diferem entre duas versões — o que o histórico chama de "alterado". */
export function changedSections(a: VisualDesign, b: VisualDesign): StudioSectionId[] {
  return (Object.keys(HISTORY_AREA_OF_SECTION) as StudioSectionId[]).filter(s => JSON.stringify(a[s]) !== JSON.stringify(b[s]));
}
